import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

import { auth, db } from "@/core/services/FirebaseService";
import {
  setDoc,
  doc,
  writeBatch,
  query,
  collection,
  limit,
  orderBy,
  where,
  onSnapshot,
  startAfter,
  Timestamp,
  GeoPoint,
  getDocs,
  getDoc,
  Query,
  DocumentData,
} from "firebase/firestore";
import { Unsubscribe } from "@firebase/util";

enum HarvestPageQuery {
  all = "all",
  finished = "finished",
  request = "request",
  receive = "receive",
  delivery = "delivery",
  process = "process",
}

export enum HarvestStage {
  finished = "finished",
  request = "request",
  receive = "receive",
  delivery = "delivery",
  process = "process",
}

export enum HarvestStatus {
  cancelled = "cancelled",
  rejected = "rejected",
  failed = "failed",
  finished = "finished",
  ongoing = "ongoing",
}

export enum SwallowNestType {
  mangkok1 = "mangkok1",
  mangkok2 = "mangkok2",
  mangkok3 = "mangkok3",
  oval1 = "oval1",
  oval2 = "oval2",
  oval3 = "oval3",
  patahan1 = "patahan1",
  patahan2 = "patahan2",
  patahan3 = "patahan3",
  sudut1 = "sudut1",
  sudut2 = "sudut2",
  sudut3 = "sudut3",
}

interface HarvestStoreState {
  searchText: string;
  doc: { [key: string]: HarvestDoc };
  pageQuery: { [key in HarvestPageQuery]: Array<string> };
  textQuery: { [key: string]: Array<string> };
  index: { [key: string]: HarvestIndex };
  indexUnsub: Unsubscribe | null;
  modal: { harvestEdit: { hid: string | null } };
  // Panen yang memilih satu superteam sebagai tujuan kirim (halaman Panen Masuk)
  superteamQuery: { stId: string | null; ids: Array<string>; unsub: Unsubscribe | null };
  // Seluruh dokumen harvests (tabel Panen admin dan hitungan panen per superteam)
  // mode: "all" (admin) atau "st:<stId>" (SuperTeam); listener milik peran lain
  // dihentikan dulu supaya ganti akun tanpa muat ulang halaman tetap benar.
  allQuery: { ids: Array<string>; unsub: Unsubscribe | null; loaded: boolean; mode?: string };
}

export enum HarvestIndexStatus {
  requestStart,
  requestCancelled,
  requestRejected,
  requestEnd,
  receiveStart,
  receiveRejected,
  receiveEnd,
  deliveryStart,
  deliveryRejected,
  deliveryEnd,
  processStart,
  processReject,
  processEnd,
  all,
  null,
}

export interface HarvestIndex {
  hid: string;
  farmer: string | null;
  rbw: string | null;
  address: string | null;
  imageURL: string | null;
  date: Timestamp | null;
  status: HarvestIndexStatus;
}

interface HarvestDoc {
  id: string;
  data: HarvestDocData;
  unsub: Unsubscribe | null;
}

interface HarvestDocData {
  hid: string;
  // Provinsi dari alamat petani/gedung, diisi trigger onHarvestScope (rules SuperTeam)
  scopeProvince?: string | null;
  stage: HarvestStage;
  status: HarvestStatus;
  lastUpdate: Timestamp | null;
  farmer: {
    email: string;
    formatedAddress: string;
    geopoint: GeoPoint | null;
    name: string;
    rbwid: string;
    rbwName: string;
    uid: string;
  };
  request: {
    harvestDate: Timestamp | null;
    harvestDetail: {
      [key in SwallowNestType]: {
        imageURL: string;
        imageURLs: Array<string>;
        weight: number;
        // Bentuk dan warna ditulis app saat petani mengajukan; app memakainya
        // untuk label dan perkiraan harga, jadi harus ikut tersimpan kembali.
        shape: string;
        color: string;
      };
    };
    harvestHumidity: number;
    requestDate: Timestamp | null;
    isRejected: boolean;
    isCancelled: boolean;
    // Ditulis aplikasi petani saat mengajukan dan membatalkan
    lastUpdate: Timestamp | null;

    note: string;
    dateStart: Timestamp | null;
    dateEnd: Timestamp | null;
    // Superteam tujuan kirim yang dipilih petani di app (tahap Pengiriman)
    superTeam: {
      stId: string;
      name: string;
      teamLeader: string;
      contact: string;
      operatingHours: string;
      address: string;
      geopoint: GeoPoint | null;
    } | null;
    // Nomor registrasi yang diisi petani di app; dipakai superteam saat mencocokkan paket
    registrationNo: string;
    superTeamChosenAt: Timestamp | null;
    // Hasil pemberitahuan ke superteam oleh backend Go saat dipilih
    superTeamNotify: {
      sentAt: Timestamp | null;
      deviceCount: number;
      pushSent: boolean;
      inboxStored: boolean;
      byAdmin: boolean;
    } | null;
  };
  receive: {
    humidity: number;
    price: number;
    isRejected: boolean;
    // Dokumen lama hanya punya status "rejected", tanpa isRejected
    status?: string;

    item: {
      [key in SwallowNestType]: {
        imageURL: string;
        imageURLs: Array<string>;
        weight: number | null;
        // Rupiah per kg; kosong pada pembelian lama yang hanya punya total.
        price?: number | null;
      };
    };

    note: string;
    dateEnd: Timestamp | null;
  };
  delivery: {
    shipmentId: string;
    factoryAddress: string;
    courier: string;
    receiptID: string;
    receiptImageURL: string;
    isFailed: boolean;
    
    item: {
      [key in SwallowNestType]: {
        imageURL: string;
        imageURLs: Array<string>;
        weight: number;
      };
    };

    price: number;
    weight: number;
    humidity: number;

    note: string;
    note2: string;
    dateStart: Timestamp | null;
    dateEnd: Timestamp | null;
  };
  process: {
    productMarketplace: { [key: string]: number };
    productOthers: { [key in SwallowNestType]: number };
    isFailed: boolean;

    note: string;
    dateStart: Timestamp | null;
    dateEnd: Timestamp | null;
    
    consumerProduct: {
      [key: string]: {
        qty: number;
      };
    };
    nestProduct: {
      [key in SwallowNestType]: {
        weight: number;
      };
    };
  };
}

// Bentuk/warna baku per kunci jenis sarang, sama dengan apps/price.swallowNest.
// Dipakai saat dokumen lama kehilangan field shape/color.
export const nestKeyShape = (key: string): string => {
  if (key.startsWith("mangkok")) return "Mangkok";
  if (key.startsWith("oval")) return "Oval";
  if (key.startsWith("patahan")) return "Patahan";
  if (key.startsWith("sudut")) return "Sudut";
  return "";
};
export const nestKeyColor = (key: string): string => {
  if (key.endsWith("1")) return "Putih Kapas";
  if (key.endsWith("2")) return "Beras";
  if (key.endsWith("3")) return "Kronis";
  return "";
};

const getHarvestStage = (harvest: HarvestDocData): HarvestStage => {
  if (harvest?.process?.dateEnd) {
    return HarvestStage.finished;
  } else if (harvest?.delivery?.dateEnd) {
    return HarvestStage.process;
  } else if (harvest?.receive?.dateEnd) {
    return HarvestStage.delivery;
  } else if (harvest?.request?.dateEnd) {
    return HarvestStage.receive;
  } else {
    return HarvestStage.request;
  }
};

export const getHarvestStatus = (harvest: HarvestDocData): HarvestStatus => {
  // Penolakan admin dulu ikut menandai isCancelled, jadi isRejected diperiksa lebih dulu.
  if (harvest?.request?.isRejected) {
    return HarvestStatus.rejected;
  } else if (harvest?.request?.isCancelled) {
    return HarvestStatus.cancelled;
  } else if (harvest?.receive?.isRejected || harvest?.receive?.status === "rejected") {
    return HarvestStatus.rejected;
  } else if (harvest?.delivery?.isFailed) {
    return HarvestStatus.failed;
  } else if (harvest?.process?.isFailed) {
    return HarvestStatus.failed;
  } if (harvest?.process?.dateEnd) {
    return HarvestStatus.finished;
  } else {
    return HarvestStatus.ongoing;
  }
};

@Module
export default class HarvestModule
  extends VuexModule
  implements HarvestStoreState
{
  errorMessage = null;
  searchText = "";
  doc = {} as { [key: string]: HarvestDoc };
  pageQuery = {} as { [key in HarvestPageQuery]: Array<string> };
  textQuery = {} as { [key: string]: Array<string> };
  index = {} as { [key: string]: HarvestIndex };
  indexUnsub = null as Unsubscribe | null;
  modal = { harvestEdit: { hid: null } } as HarvestStoreState["modal"];
  superteamQuery = {
    stId: null,
    ids: [],
    unsub: null,
  } as HarvestStoreState["superteamQuery"];
  allQuery = {
    ids: [],
    unsub: null,
    loaded: false,
    mode: undefined,
  } as HarvestStoreState["allQuery"];

  @Mutation
  [Mutations.HARVEST_SET_MODAL_EDIT](hid: string) {
    this.modal.harvestEdit.hid = hid ?? null
  }

  @Mutation
  [Mutations.HARVEST_SET_INDEX](payload: {
    id: string;
    data: DocumentData;
  }) {
    const id = payload.id;
    const data = payload.data;

    const docData = {
      hid: id,
      
      email: data.email ?? null,
      farmer: data.farmer ?? null,
      rbw: data.rbw ?? null,
      address: data.address ?? null,
  
      imageURL: data.imageURL ?? null,
      date: data.date ?? null,
      status: data.status ?? HarvestIndexStatus.null,
    } as HarvestIndex;

    if(id) this.index[id] = docData
    // console.log(docData)
  }

  @Mutation
  [Mutations.HARVEST_SET_INDEX_SUB](payload: {
    unsub: Unsubscribe;
  }) {
    const unsub = payload.unsub;
    this.indexUnsub = unsub ?? null
  }

  @Mutation
  [Mutations.HARVEST_SET_DOC](payload: {
    id: string;
    data: DocumentData;
    unsub: Unsubscribe | undefined;
  }) {
    const id = payload.id;
    const data = payload.data;
    const unsub = payload.unsub;

    const docData = {
      hid: id,
      stage: getHarvestStage(data as HarvestDocData),
      status: getHarvestStatus(data as HarvestDocData),
      lastUpdate: data.lastUpdate ?? null,
      farmer: {
        email: data.farmer?.email ?? "",
        formatedAddress: data.farmer?.formatedAddress ?? "",
        geopoint: data.farmer?.geopoint ?? null,
        name: data.farmer?.name ?? "",
        rbwid: data.farmer?.rbwId ?? data.farmer?.rbwid ?? "",
        rbwName: data.farmer?.rbwName ?? "",
        uid: data.farmer?.uid ?? "",
      },
      request: {
        harvestDate: data.request?.harvestDate ?? data.request?.requestDate ?? null,
        harvestDetail: Object.entries<DocumentData>(
          data.request?.harvestDetail ?? {}
        ).reduce((res, cV) => {
          if (cV[0] in SwallowNestType)
            return {
              ...res,
              [cV[0]]: {
                imageURL: cV[1]?.imageURL ?? "",
                imageURLs: cV[1]?.imageURLs ?? [],
                weight: cV[1]?.weight ?? "",
                shape: cV[1]?.shape ?? nestKeyShape(cV[0]),
                color: cV[1]?.color ?? nestKeyColor(cV[0]),
              },
            };
          return res;
        }, {}),
        harvestHumidity: data.request?.harvestHumidity ?? null,
        requestDate: data.request?.requestDate ?? null,
        isRejected: data.request?.isRejected ?? false,
        isCancelled: data.request?.isCancelled ?? false,
        lastUpdate: data.request?.lastUpdate ?? null,

        note: data.request?.note ?? "",
        dateStart: data.request?.requestDate ?? data.request?.dateStart ?? null,
        dateEnd: data.request?.dateEnd ?? null,
        superTeam: data.request?.superTeam
          ? {
              stId: data.request.superTeam.stId ?? "",
              name: data.request.superTeam.name ?? "",
              teamLeader: data.request.superTeam.teamLeader ?? "",
              contact:
                typeof data.request.superTeam.contact === "string"
                  ? data.request.superTeam.contact
                  : data.request.superTeam.contact?.whatsapp ?? "",
              operatingHours: data.request.superTeam.operatingHours ?? "",
              address:
                data.request.superTeam.location?.formatedAddress ??
                data.request.superTeam.location?.address ??
                "",
              geopoint: data.request.superTeam.location?.geopoint ?? null,
            }
          : null,
        registrationNo: data.request?.registrationNo ?? "",
        superTeamChosenAt: data.request?.superTeamChosenAt ?? null,
        superTeamNotify: data.request?.superTeamNotify
          ? {
              sentAt: data.request.superTeamNotify.sentAt ?? null,
              deviceCount: data.request.superTeamNotify.deviceCount ?? 0,
              pushSent: data.request.superTeamNotify.pushSent ?? false,
              inboxStored: data.request.superTeamNotify.inboxStored ?? false,
              byAdmin: data.request.superTeamNotify.byAdmin ?? false,
            }
          : null,
      },
      receive: {
        price: data.receive?.price ?? 0,
        humidity: data.receive?.humidity ?? 0,
        isRejected: !!data.receive?.isRejected || data.receive?.status === "rejected",

        item: Object.entries<DocumentData>(
          data.receive?.item ?? {}
        ).reduce((res, cV) => {
          if (cV[0] in SwallowNestType)
            return {
              ...res,
              [cV[0]]: {
                imageURL: cV[1]?.imageURL ?? "",
                imageURLs: cV[1]?.imageURLs ?? [],
                weight: cV[1]?.weight ?? null,
                price: cV[1]?.price ?? null,
              },
            };
          return res;
        }, {}),

        note: data.receive?.note ?? "",
        dateEnd: data.receive?.dateEnd ?? null,
      },
      delivery: {
        shipmentId: data.delivery?.shipmentId ?? "",
        factoryAddress: data.delivery?.factoryAddress ?? "",
        courier: data.delivery?.courier ?? "",
        receiptID: data.delivery?.receiptID ?? "",
        receiptImageURL: data.delivery?.receiptImageURL ?? "",
        
        item: Object.entries<DocumentData>(
          data.delivery?.item ?? {}
        ).reduce((res, cV) => {
          if (cV[0] in SwallowNestType)
            return {
              ...res,
              [cV[0]]: {
                imageURL: cV[1]?.imageURL ?? "",
                imageURLs: cV[1]?.imageURLs ?? [],
                weight: cV[1]?.weight ?? "",
              },
            };
          return res;
        }, {}),

        price: data.delivery?.price ?? 0,
        weight: data.delivery?.weight ?? 0,
        humidity: data.delivery?.humidity ?? 0,
        isFailed: data.delivery?.isFailed ?? false,

        note: data.delivery?.note ?? "",
        note2: data.delivery?.note2 ?? "",
        dateStart: data.delivery?.dateStart ?? null,
        dateEnd: data.delivery?.dateEnd ?? null,
      },
      process: {
        productMarketplace: data.process?.productMarketplace ?? {},
        productOthers: data.process?.productOthers ?? {},
        isFailed: data.process?.isFailed ?? false,

        note: data.process?.note ?? "",
        dateStart: data.process?.dateStart ?? null,
        dateEnd: data.process?.dateEnd ?? null,

        consumerProduct: Object.entries<DocumentData>(
          data.process?.consumerProduct ?? {}
        ).reduce((res, cV) => {
          return {
            ...res,
            [cV[0]]: {
              qty: cV[1]?.qty ?? 0,
            },
          };
        }, {}),
        nestProduct: Object.entries<DocumentData>(
          data.process?.nestProduct ?? {}
        ).reduce((res, cV) => {
          if (cV[0] in SwallowNestType)
            return {
              ...res,
              [cV[0]]: {
                qty: cV[1]?.qty ?? 0,
              },
            };
          return res;
        }, {}),
      },
    } as HarvestDocData;

    if (unsub) {
      this.doc[id] = {
        id: id,
        data: docData,
        unsub: unsub,
      };
    } else if (this.doc[id]) {
      this.doc[id].data = docData;
    } else {
      // Hasil query daftar (mis. Panen Masuk) belum punya langganan per dokumen;
      // simpan dulu, HARVEST_GET_SINGLE akan memasang unsub saat dibuka.
      this.doc[id] = { id: id, data: docData, unsub: null };
    }
  }
  @Action({ rawError: true })
  [Actions.HARVEST_GET_SINGLE](payload: { hid: string }) {
    const hid = payload.hid;
    if (!this.doc[hid]?.unsub) {
      const unsub = onSnapshot(doc(db, "harvests", hid), (dbDoc) => {
        console.log("HARVEST_GET_SINGLE", dbDoc.data()?.farmer?.rbwId)
        this.context.dispatch(Actions.RBW_GET_SINGLE, { rbwid: dbDoc.data()?.farmer?.rbwId })
        this.context.dispatch(Actions.ACCOUNT_GET_SINGLE, { uid: dbDoc.data()?.farmer?.uid })
        this.context.commit(Mutations.HARVEST_SET_DOC, {
          id: dbDoc.id,
          data: dbDoc.data() ?? {},
          unsub: this.doc[hid]?.unsub ? null : unsub,
        });
      });
    }
  }

  @Mutation
  [Mutations.HARVEST_SET_PAGE_QUERY](payload: {
    key: HarvestPageQuery;
    ids: Array<string>;
    append: boolean;
  }) {
    const key = payload.key;
    const ids = payload.ids;
    const append = payload.append;

    if (append && this.pageQuery[key]) {
      this.pageQuery[key] = [...this.pageQuery[key], ...ids];
    } else {
      this.pageQuery[key] = ids;
    }
  }

  @Mutation
  [Mutations.HARVEST_SET_SEARCH_TEXT](payload: string) {
    this.searchText = payload
  }

  @Mutation
  [Mutations.HARVEST_SET_TEXT_QUERY](payload: {
    key: string;
    ids: Array<string>;
    append: boolean;
  }) {
    const key = payload.key;
    const ids = payload.ids;
    const append = payload.append;

    if (append && this.textQuery[key]) {
      this.textQuery[key] = [...this.textQuery[key], ...ids];
    } else {
      this.textQuery[key] = ids;
    }
  }

  @Action
  [Actions.HARVEST_POPULATE_PAGE_QUERY](payload: {
    key: HarvestPageQuery;
    append: boolean;
  }) {
    const key = payload.key;
    const append = payload.append;

    let dbQuery: Query<DocumentData>;

    const harvestIndexQuery = query(
      collection(db, "queryIndices"),
      where("target", "==", "harvest"),
      where("total", "!=", 0),
    );
    this.context.commit(Mutations.HARVEST_SET_INDEX_SUB, {
      unsub: onSnapshot(harvestIndexQuery, (userIndexDocs) => {
        userIndexDocs.docs.forEach((x)=>{
          Object.entries(x.data().harvest).forEach((y) => {
            this.context.commit(Mutations.HARVEST_SET_INDEX, {
              id: y[0],
              data: y[1] as DocumentData,
            });
          });
        })
      }, (e)=>console.log(e)),
    });
  }

  @Mutation
  [Mutations.HARVEST_SET_SUPERTEAM_QUERY](payload: {
    stId: string | null;
    ids: Array<string>;
    unsub: Unsubscribe | null;
  }) {
    if (this.superteamQuery.unsub && this.superteamQuery.unsub !== payload.unsub) {
      this.superteamQuery.unsub();
    }
    this.superteamQuery = {
      stId: payload.stId,
      ids: payload.ids,
      unsub: payload.unsub,
    };
  }

  // Berlangganan daftar panen yang memilih superteam `stId` sebagai tujuan kirim.
  // Dipakai halaman Panen Masuk (akun superteam melihat miliknya sendiri,
  // admin bisa memilih tim). Butuh indeks komposit
  // harvests(request.superTeam.stId ASC, request.requestDate DESC).
  @Action
  [Actions.HARVEST_POPULATE_SUPERTEAM_QUERY](payload: { stId: string | null }) {
    const stId = payload.stId;
    if (!stId) {
      this.context.commit(Mutations.HARVEST_SET_SUPERTEAM_QUERY, {
        stId: null,
        ids: [],
        unsub: null,
      });
      return;
    }
    const dbQuery = query(
      collection(db, "harvests"),
      where("request.superTeam.stId", "==", stId),
      orderBy("request.requestDate", "desc"),
      limit(200)
    );
    const unsub = onSnapshot(
      dbQuery,
      (snapshot) => {
        snapshot.docs.forEach((dbDoc) => {
          this.context.commit(Mutations.HARVEST_SET_DOC, {
            id: dbDoc.id,
            data: dbDoc.data() ?? {},
            unsub: undefined,
          });
        });
        this.context.commit(Mutations.HARVEST_SET_SUPERTEAM_QUERY, {
          stId,
          ids: snapshot.docs.map((d) => d.id),
          unsub,
        });
      },
      (e) => console.error("HARVEST_POPULATE_SUPERTEAM_QUERY", e)
    );
    this.context.commit(Mutations.HARVEST_SET_SUPERTEAM_QUERY, {
      stId,
      ids: this.superteamQuery.stId === stId ? this.superteamQuery.ids : [],
      unsub,
    });
  }

  @Mutation
  [Mutations.HARVEST_SET_ALL_QUERY](payload: {
    ids: Array<string>;
    unsub: Unsubscribe | null;
    loaded: boolean;
    mode?: string;
  }) {
    this.allQuery = {
      ids: payload.ids,
      unsub: payload.unsub ?? this.allQuery.unsub,
      loaded: payload.loaded,
      mode: payload.mode ?? this.allQuery.mode,
    };
  }

  // Menghentikan listener tabel Panen (ganti peran/akun, atau listener ditolak
  // rules setelah logout) supaya populate berikutnya memulai ulang.
  @Mutation
  [Mutations.HARVEST_RESET_ALL_QUERY]() {
    this.allQuery.unsub?.();
    this.allQuery = { ids: [], unsub: null, loaded: false, mode: undefined };
  }

  // Berlangganan seluruh dokumen harvests sekali untuk tabel Panen (detail isi
  // panen, harga, superteam, linimasa) dan hitungan panen per superteam. Tanpa
  // where/orderBy agar tidak butuh indeks komposit; pengurutan di sisi klien.
  @Action
  [Actions.HARVEST_POPULATE_ALL_QUERY]() {
    if (this.allQuery.unsub && this.allQuery.mode === "all") return;
    if (this.allQuery.unsub) this.context.commit(Mutations.HARVEST_RESET_ALL_QUERY);
    const unsub = onSnapshot(
      collection(db, "harvests"),
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "removed") return;
          this.context.commit(Mutations.HARVEST_SET_DOC, {
            id: change.doc.id,
            data: change.doc.data() ?? {},
            unsub: undefined,
          });
        });
        this.context.commit(Mutations.HARVEST_SET_ALL_QUERY, {
          ids: snapshot.docs.map((d) => d.id),
          unsub,
          loaded: true,
        });
      },
      (e) => {
        console.error("HARVEST_POPULATE_ALL_QUERY", e);
        this.context.commit(Mutations.HARVEST_RESET_ALL_QUERY);
      }
    );
    this.context.commit(Mutations.HARVEST_SET_ALL_QUERY, {
      ids: this.allQuery.ids,
      unsub,
      loaded: false,
      mode: "all",
    });
  }

  // Versi HARVEST_POPULATE_ALL_QUERY untuk akun SuperTeam: firestore.rules hanya
  // mengizinkan panen yang dikirim ke timnya atau yang scopeProvince-nya sama
  // dengan provinsi tim, jadi dua query itu digabung (tanpa indeks komposit).
  @Action
  async [Actions.HARVEST_POPULATE_SCOPED_QUERY](payload: { stId: string }) {
    const mode = `st:${payload.stId}`;
    if (this.allQuery.unsub && this.allQuery.mode === mode) return;
    if (this.allQuery.unsub) this.context.commit(Mutations.HARVEST_RESET_ALL_QUERY);
    const stSnap = await getDoc(doc(db, "superteams", payload.stId));
    const province: string | undefined = stSnap.data()?.scopeProvince;

    const idsBySource: Record<string, string[]> = {};
    const unsubs: Unsubscribe[] = [];
    const unsub = () => unsubs.forEach((u) => u());
    const listen = (source: string, dbQuery: Query<DocumentData>) =>
      onSnapshot(
        dbQuery,
        (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            if (change.type === "removed") return;
            this.context.commit(Mutations.HARVEST_SET_DOC, {
              id: change.doc.id,
              data: change.doc.data() ?? {},
              unsub: undefined,
            });
          });
          idsBySource[source] = snapshot.docs.map((d) => d.id);
          this.context.commit(Mutations.HARVEST_SET_ALL_QUERY, {
            ids: Array.from(new Set(Object.values(idsBySource).flat())),
            unsub,
            loaded: true,
          });
        },
        (e) => {
          console.error("HARVEST_POPULATE_SCOPED_QUERY", source, e);
          this.context.commit(Mutations.HARVEST_RESET_ALL_QUERY);
        }
      );

    unsubs.push(listen("team", query(collection(db, "harvests"), where("request.superTeam.stId", "==", payload.stId))));
    if (province) {
      unsubs.push(listen("province", query(collection(db, "harvests"), where("scopeProvince", "==", province))));
    }
    this.context.commit(Mutations.HARVEST_SET_ALL_QUERY, {
      ids: this.allQuery.ids,
      unsub,
      loaded: false,
      mode,
    });
  }

  @Action
  async [Actions.HARVEST_POPULATE_TEXT_QUERY](payload: {
    key: string;
    append: boolean;
  }) {
    const key = payload.key;
    const append = payload.append;

    const dbQuery = query(
      collection(db, "harvests"),
      where("versionNo", "==", 0),
      orderBy("farmerName"),
      where("farmerName", "==", key),
      where("farmerName", "==", key + "~"),
      // limit(50)
    );

    const dbDocs = await getDocs(dbQuery);
    const ids: Array<string> = [];

    dbDocs.forEach((dbDoc) => {
      ids.push(dbDoc.id);
      if (!this.doc[dbDoc.id]) {
        this.context.commit(Mutations.HARVEST_SET_DOC, {
          id: dbDoc.id,
          data: dbDoc.data(),
          unsub: onSnapshot(doc(db, "harvests", dbDoc.id), (dbDoc) => {
            this.context.commit(Mutations.HARVEST_SET_DOC, {
              id: dbDoc.id,
              data: dbDoc.data(),
            });
          }),
        });
      }
    });

    this.context.commit(Mutations.HARVEST_SET_TEXT_QUERY, {
      key: key,
      ids: ids,
      append: append,
    });
  }

  @Action
  [Actions.HARVEST_UPDATE_DOC](payload: {
    id: string;
    harvest: { [x: string]: any };
  }) {
    const id = payload.id;
    const harvest = payload.harvest;

    // Get a new write batch
    const batch = writeBatch(db);

    // Set profile value
    const harvestRef = doc(db, "harvests", id);
    batch.update(harvestRef, harvest);

    console.log(id, harvest);

    // Commit the batch
    return batch.commit();
  }

  @Action
  async [Actions.HARVEST_CREATE](payload: {
    id: string;
    harvest: DocumentData;
  }) {
    const id = payload.id;
    const harvest = payload.harvest;

    // Get a new write batch
    const batch = writeBatch(db);

    if (harvest) {
      // Set profile value
      const harvestRef = doc(db, "harvests", id);
      batch.set(harvestRef, {
        versionNo: 0,
        ...harvest,
      });
    }

    // Commit the batch
    return batch.commit();
  }
}

export { HarvestPageQuery, HarvestStoreState, HarvestDoc, HarvestDocData };
